import React from 'react'
import * as R from 'ramda'
import { t } from 'ttag'

import { copyToClipboard } from 'utils/clipboard'
import { useAddEvaluatorModal } from './useAddEvaluatorModal'
import { useEditEvaluatorModal } from './useEditEvaluatorModal'
import { useInviteEvaluatorModal } from './useInviteEvaluatorModal'
import { useDeleteEvaluatorModal } from './useDeleteEvaluatorModal'

// NOTE: hook that exposes evaluator state and handlers
// for other local hooks and interview components
export const useEvaluator = (state, handlers) => {
  const { evaluation, evaluators, id, user } = state
  const {
    handleDeleteEvaluator,
    handleFetch,
    handlePostEvaluator,
    handleSetResponse,
    handleShowSnackbar,
  } = handlers

  const [evaluatorId, setEvaluatorId] = React.useState(null)

  const evaluator = React.useMemo(() => {
    const index = R.findIndex(R.propEq('id', evaluatorId))(evaluators)

    return R.gte(index, 0) ? evaluators[index] : null
  }, [evaluatorId, evaluators])

  const handleAddEvaluator = React.useCallback(
    async ({ email, name }) => {
      const { error, meta, response } = await handlePostEvaluator({
        jobInterview: id,
        email,
        name,
      })

      if (response) {
        const { response } = await handleFetch({ uuid: id })

        handleSetResponse(response)
        handleShowSnackbar({
          message: t`The evaluator added successfully.`,
          type: 'success',
        })
      }

      return { error, meta, response }
    },
    [
      id,
      handleFetch,
      handlePostEvaluator,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleAddSelf = React.useCallback(() => {
    handleAddEvaluator({
      evaluators: [{ email: user?.email, name: user?.name }],
    })
  }, [user, handleAddEvaluator])

  const handleCopyLink = React.useCallback(
    link => {
      copyToClipboard(link, {
        onErrorAction: () =>
          handleShowSnackbar({
            message: t`Your browser doesn't support the action. Please update your browser to the latest version.`,
            type: 'error',
          }),
        onSuccessAction: () =>
          handleShowSnackbar({
            message: t`Successfully copied the link.`,
            type: 'success',
          }),
      })
    },
    [handleShowSnackbar],
  )

  const handleRemoveEvaluator = React.useCallback(
    async ({ uuid }) => {
      const { error, response } = await handleDeleteEvaluator({ uuid })

      if (response) {
        const { response } = await handleFetch({ uuid: id })

        handleSetResponse(response)
        handleShowSnackbar({
          message: t`The evaluator deleted successfully.`,
          type: 'success',
        })
      } else {
        console.log({ error })
      }
    },
    [
      id,
      handleDeleteEvaluator,
      handleFetch,
      handleSetResponse,
      handleShowSnackbar,
    ],
  )

  const handleSetEvaluatorId = React.useCallback(
    id => {
      setEvaluatorId(id)
    },
    [setEvaluatorId],
  )

  const hasEvaluators = R.gt(R.length(evaluators), 0)
  const isAlreadyIncluded = R.any(
    ({ email }) => R.equals(email, user?.email),
    evaluators,
  )
  const isEvaluationAvailable = Boolean(evaluation)
  const isSelfAdded = R.or(isEvaluationAvailable, isAlreadyIncluded)

  const {
    handleAddEvaluatorClose,
    handleAddEvaluatorOpen,
    isAddEvaluatorOpen,
  } = useAddEvaluatorModal()

  const {
    handleDeleteEvaluatorClose,
    handleDeleteEvaluatorOpen,
    handleDeleteEvaluatorConfirm,
    isDeleteEvaluatorOpen,
  } = useDeleteEvaluatorModal(
    { evaluator },
    { handleRemoveEvaluator, handleSetEvaluatorId },
  )

  const {
    handleEditEvaluatorClose,
    handleEditEvaluatorOpen,
    handleEditEvaluatorConfirm,
    isEditEvaluatorOpen,
  } = useEditEvaluatorModal({ evaluator }, { handleSetEvaluatorId })

  const {
    handleInviteEvaluatorClose,
    handleInviteEvaluatorOpen,
    handleInviteEvaluatorConfirm,
    isInviteEvaluatorOpen,
  } = useInviteEvaluatorModal()

  return {
    state: {
      evaluator,
      hasEvaluators,
      isAddEvaluatorOpen,
      isDeleteEvaluatorOpen,
      isEditEvaluatorOpen,
      isInviteEvaluatorOpen,
      isSelfAdded,
    },
    handlers: {
      handleAddEvaluatorClose,
      handleAddEvaluatorOpen,
      handleAddEvaluator,
      handleDeleteEvaluatorClose,
      handleDeleteEvaluatorOpen,
      handleDeleteEvaluatorConfirm,
      handleEditEvaluatorClose,
      handleEditEvaluatorOpen,
      handleEditEvaluatorConfirm,
      handleInviteEvaluatorClose,
      handleInviteEvaluatorOpen,
      handleInviteEvaluatorConfirm,
      handleAddSelf,
      handleCopyLink,
    },
  }
}
